// The home-page blocks of a theme (the "home" array of the theme file, see docs/themes/THEME-SPEC.md) and what the
// teacher wrote into them (branding.homeJson, see HomeContent on the backend). Both arrive as text and are read
// defensively: a bad value only means "no blocks" / "no content", never a broken page.

export const BLOCKS = [
  "heroCta", "trustStats", "featureGrid", "programCards", "pricingPlans", "teamGrid", "coachBio",
  "testimonials", "faq", "appBadges", "ctaBand",
  // the newer themes (see HomeBlocks on the backend)
  "hero", "subjectFeature", "steps", "liveSessions", "freeLessons", "honorBoard",
];

function parse(value) {
  if (!value) return null;
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch (e) {
    return null;
  }
}

/** The ordered block names of the tenant's theme, or null when the theme has none (the usual school home page). */
export function homeBlocksOf(branding) {
  const def = parse(branding && branding.themeDefinition);
  const list = def && Array.isArray(def.home) ? def.home.filter((b) => BLOCKS.indexOf(b) >= 0) : [];
  return list.length ? list : null;
}

/** What the teacher wrote into the blocks; every list is an array, every text a string. */
export function homeContentOf(branding) {
  const raw = parse(branding && branding.homeJson) || {};
  const list = (v) => (Array.isArray(v) ? v.filter((x) => x && typeof x === "object") : []);
  const str = (v) => (typeof v === "string" ? v : "");
  const apps = raw.apps && typeof raw.apps === "object" ? raw.apps : {};
  return {
    heroCtaLabel: str(raw.heroCtaLabel),
    ctaTitle: str(raw.ctaTitle),
    ctaText: str(raw.ctaText),
    stats: list(raw.stats),
    features: list(raw.features),
    testimonials: list(raw.testimonials),
    faq: list(raw.faq),
    apps: { ios: webUrl(apps.ios), android: webUrl(apps.android) },
    steps: list(raw.steps),
    honor: list(raw.honor),
    subjectEyebrow: str(raw.subjectEyebrow),
    subjectTitle: str(raw.subjectTitle),
    subjectText: str(raw.subjectText),
    subjectItems: list(raw.subjectItems),
  };
}

/** The theme's layout settings the blocks are drawn with ("none" for themes that predate them). */
export function themeLayoutOf(branding) {
  const def = parse(branding && branding.themeDefinition);
  const layout = (def && def.layout) || {};
  return {
    hero: layout.hero || "split",
    art: layout.art || "none",
    subject: layout.subject || "none",
    style: layout.style || "none",
  };
}

/** Only http(s) links are ever put in an href (the server checks too). */
export function webUrl(value) {
  return typeof value === "string" && /^https?:\/\//i.test(value.trim()) ? value.trim() : "";
}

/** Which of the tenant's blocks need the plans and the trainers fetched. */
export function needsPlans(blocks, branding) {
  if (!blocks) return false;
  // a fitness theme's own section is its membership plans
  const subjectPlans = blocks.indexOf("subjectFeature") >= 0 && themeLayoutOf(branding).subject === "plans";
  return blocks.indexOf("pricingPlans") >= 0 || subjectPlans;
}

/** The signed-in student's coming sessions are only fetched when the theme shows them. */
export function needsMeetings(blocks) {
  return !!blocks && blocks.indexOf("liveSessions") >= 0;
}

export function needsInstructors(blocks) {
  return !!blocks && (blocks.indexOf("teamGrid") >= 0 || blocks.indexOf("coachBio") >= 0);
}

/** "250 EGP / month", "1,500 EGP / 3 months", "Free". lang is "ar" or "en". */
export function planPrice(plan, lang) {
  const price = Number(plan && plan.price);
  if (!price) return lang === "en" ? "Free" : "مجانًا";
  // Western digits in both languages, like every other number on the site
  const amount = price.toLocaleString("en-US", { maximumFractionDigits: 2 });
  const months = Number(plan.intervalMonths) || 1;
  const every =
    lang === "en" ? (months === 1 ? "month" : `${months} months`) : months === 1 ? "شهر" : months === 2 ? "شهرين" : `${months} شهور`;
  return `${amount} ${plan.currency || ""} / ${every}`.replace("  ", " ");
}

/** The membership of the plan in the member's list (the latest one that is still running), or null. */
export function membershipOf(mine, planId) {
  const running = (mine || []).filter((m) => m.planId === planId && (m.status === "ACTIVE" || m.status === "TRIAL" || m.status === "PENDING"));
  return running.length ? running[0] : null;
}
